import React, { useEffect, useState, useCallback, ReactElement } from 'react';
import { lang, isSystem } from '@umu/ukit-lang';
import {
    SessionMessageAction,
    IIframeSessionTriggerClickACKChild,
    SessionTriggerClickTypes,
} from '@umu/umu-iframe/src/session/messageActions';

import * as ua from '@umu/umu-ua';
import { $window, isInIframe } from '@umu/umu-dom';
import { debounce, openWindow, formatNumToMoney } from '@umu/umu-util';
import { isFromCourseMiniApp, isInPinIFrame } from '../../@utils/umuUtil';
import Reward from '../@reward/reward';
import ToggleCollection from './components/@toggleCollection/toggleCollection';
import { ECollectionType } from '@umu/umu-types';
import { getSiblingsNav } from '@umu/umu-api';
import {
    IsTeacher,
    pointStatus,
    ISiblingsNavData,
    IsAllowReward,
    IsCollectionAdd,
    IHideSessionNav,
} from '@umu/umu-api/src/services/element/siblingsDataService';
import {
    useMobileIframeLandscapeDisplayManager,
    tryIframeRegisterPostLoginAlwaysResolve,
} from '../../services/responsiveDisplay/course';
import { isParentLocationProgram } from '../../services/responsiveDisplay/helper';
import Share from './components/@share';
import { Tooltip, Button, Iconfont } from '@umu/umu-design';
import { childPostControl } from '@umu/umu-iframe/src/session/postMessage/childPostControl';
import { composePcSessionUrl } from '../../services/courseUtil';
import { getCurrencySymbol } from '../../@utils/umuUtil';
import { services, config, components } from 'common';
const { SESSION_TYPES } = config;
const { If } = components;

import classnames from 'classnames';
import './siblingsNav.scss';
import { COURSE_CATALOG_DRAWER_ID, CourseCatalogDrawer } from './components/@courseCatalogDrawer';

enum NavItemType {
    POINTS = 'points',
    REWARD = 'reward',
    DIRECTORY = 'directory',
    BACK_DIRECTORY = 'backDirectory',
    PREV = 'prev',
    NEXT = 'next',
    MENU = 'menu',
    COLLECTION = 'collection',
    ENROLLMENT = 'enrollment',
    PREV_COURSE = 'prev_course',
    NEXT_COURSE = 'next_course',
}

interface IProps {
    parentId: number;
    elementId: number;
    payChannel?: string[];
    beforeClickArrowCallback?: Function | undefined;
}

interface INavItem {
    type: NavItemType;
    isShow?: boolean;
    labelText: string | ReactElement;
    data: { [propName: string]: any };
}
const { getModuleData } = services;
const isInProgram = isParentLocationProgram();

/*
    部分浏览器在滚动页面时会显示/隐藏工具栏，影响视口高度。在有底部工具栏的情况下，不做iPhoneX的fix。100为经验值
 */
function isHasIphoneXBottomToolbar() {
    if (!(ua.isIphoneX && ua.isIphoneXR && ua.isIphoneXSMax)) {
        return false;
    }
    const scale = $window.innerWidth / $window.screen.width; //  放大的情况
    return $window.screen.height - $window.innerHeight / scale >= 100;
}
const getNavSwitch = (info: { siblingsInfo: ISiblingsNavData }): Record<string, boolean> => {
    const { is_teacher, is_open, setup, course_trial_status, user_enroll, enroll } = info.siblingsInfo;
    const isEnroll = !!enroll;
    const isUserEnroll = !!user_enroll;
    //开启小节试学
    const isSessionTrail = isUserEnroll && !!Number(user_enroll.trial_status);
    // 报名状态
    const isEnrollStatus = isEnroll && !!Number(enroll.status);
    // 报名成功
    const isEnrollSuccess = isUserEnroll && Number(user_enroll.enroll_status) === 2;
    //课程试学
    const isCourseTrail = !!course_trial_status && !isEnrollSuccess;
    // 是否开启赞赏
    let isRewardOpen = setup.allowReward === IsAllowReward.true;
    if (!isSystem('CN') || (ua.isIos && ua.isUMUWebview)) {
        isRewardOpen = false;
    }
    // 是否单节模式：控制是否显示上下一节导航
    const isSiblingsOpen = setup.hideSessionNav === IHideSessionNav.false;
    // app 里隐藏
    // 是否讲师身份：控制是否显示积分
    const isTeacher = is_teacher === IsTeacher.true;
    // 是否开启积分
    const isPointsOpen = is_open !== pointStatus.close; // 默认值为字符串，但修改过后会为数字

    return {
        isEnroll,
        isReward: isRewardOpen && (!isSessionTrail || !isEnrollStatus || isEnrollSuccess),
        isUserEnroll,
        isPoint: !isTeacher && isPointsOpen && (!isSessionTrail || !isEnrollStatus || isEnrollSuccess),
        isSessionTrail,
        isEnrollStatus,
        isEnrollSuccess,
        isCourseTrail,
        isRewardOpen,
        isSiblingsOpen,
        isTeacher,
        isPointsOpen,
        isBackDirectory: !isInProgram,
    };
};
/**
 * 报名按钮展示文案
 */
const getEnrollmentBtnLabel = (
    switchInfo: Record<string, boolean>,
    siblingsInfo: ISiblingsNavData,
): [string | JSX.Element, boolean] => {
    let enrollmentLabel = lang('立即报名');
    const { isSessionTrail, isEnrollStatus, isEnrollSuccess } = switchInfo;
    const { enroll, user_enroll } = siblingsInfo;
    const isEnroll = !!enroll;
    const isUserEnroll = !!user_enroll;
    if (isEnroll && enroll.setupInfo) {
        const currencySymbol = getCurrencySymbol();
        const amount = Number(enroll.setupInfo.payment.amount);
        const switchStatus = Number(enroll.setupInfo.payment.switch_status);
        let fee = amount ? formatNumToMoney(amount / 100, isSystem('JP') ? 0 : 2) : amount;
        if (!switchStatus) {
            fee = 0;
        }
        if (fee) {
            enrollmentLabel = (
                <div className="enroll-info-price">
                    {fee && (
                        <span className="price-fee">
                            {currencySymbol}
                            {fee}
                        </span>
                    )}
                    <span>{lang('立即报名')}</span>
                </div>
            );
        }
    }
    if (isUserEnroll) {
        const enrollStatus = Number(user_enroll.enroll_status);
        if (enrollStatus === 3) {
            // 报名审核中
            enrollmentLabel = lang('报名审核中');
        }
        if (enrollStatus === 4) {
            // 报名未通过
            enrollmentLabel = lang('报名未通过');
        }
    }
    const labelText = (
        <Button
            type="primary"
            onClick={() => {
                const urls = (isEnroll && enroll.shareUrl) || (isUserEnroll && user_enroll.enroll_url) || '';
                urls && $window.open(urls);
            }}
        >
            {enrollmentLabel}
        </Button>
    );
    const isShow = isSessionTrail && isEnrollStatus && !isEnrollSuccess;
    return [labelText, isShow];
};
/*
 * 获取展示按钮列表
 */
function useGetNavListData(info: {
    siblingsInfo: ISiblingsNavData | null;
    isDisplayInIframeModeForCourse: boolean;
    parentId: number;
    isNotShowDirectory: boolean;
}) {
    const { siblingsInfo, isDisplayInIframeModeForCourse, parentId, isNotShowDirectory } = info;
    const [prevCourse, setPrevCourse] = useState<{ url: string; id: string } | null>(null);
    const [nextCourse, setNextCourse] = useState<{ url: string; id: string } | null>(null);
    const isInProgram = isParentLocationProgram();

    useEffect(() => {
        childPostControl.onMessage((message: { action: SessionMessageAction; data: any }) => {
            switch (message.action) {
                case SessionMessageAction.down_next_course:
                    message.data.id && setNextCourse(message.data);
                    break;
                case SessionMessageAction.down_prev_course:
                    message.data.id && setPrevCourse(message.data);
                    break;
                default:
                    break;
            }
        });
    }, []);

    // 工具条数据分为三类
    const ret = {
        menu: [] as INavItem[],
        groupMain: [] as INavItem[],
        groupNav: [] as INavItem[],
    };
    if (!siblingsInfo) {
        return ret;
    }
    const navSwitch = getNavSwitch({ siblingsInfo });
    // 报名
    const [labelText, isShowEnrollment] = getEnrollmentBtnLabel(navSwitch, siblingsInfo);
    const enrollmentItem = {
        data: {},
        isShow: isShowEnrollment,
        type: NavItemType.ENROLLMENT,
        labelText,
    };
    // 积分
    const pointsItem = {
        type: NavItemType.POINTS,
        isShow: navSwitch.isPoint && !isFromCourseMiniApp(),
        labelText: isDisplayInIframeModeForCourse ? lang('积分') : '',
        data: {
            totalScore: siblingsInfo.points.total_points,
            url: `/course/mycpt/?group_id=${parentId}`,
        },
    };

    // 赞赏
    const rewardItem = {
        type: NavItemType.REWARD,
        isShow: navSwitch.isReward && !isFromCourseMiniApp(),
        labelText: lang('赞赏'),
        data: {},
    };
    // 目录
    const directoryItem = {
        type: NavItemType.DIRECTORY,
        isShow: !isInProgram && (navSwitch.isSiblingsOpen || isShowEnrollment) && !isNotShowDirectory,
        labelText: (
            <div className="course-directory">
                {lang('目录')}
                {navSwitch.isCourseTrail && <span className="online-trial-label">{lang('试学')}</span>}
            </div>
        ),
        data: {},
    };

    // 上一节
    let prevItem: INavItem = {
        type: NavItemType.PREV,
        labelText: lang('上一节'),
        isShow: navSwitch.isSiblingsOpen || isShowEnrollment,
        data: {
            id: siblingsInfo.siblings.pre_session_id || '',
            url: siblingsInfo.siblings.prev || '',
            type: siblingsInfo.siblings.prev_type || '',
        },
    };
    // 返回课程目录
    const lastSessionItem = {
        type: NavItemType.BACK_DIRECTORY,
        isShow: navSwitch.isBackDirectory,
        labelText: lang('返回课程目录'),
        data: {
            url: siblingsInfo.siblings.course_url || '',
        },
    };
    // 下一节 --课程目录页面下最后一节展示 返回课程目录
    let nextItem: INavItem =
        !!siblingsInfo.siblings.next || directoryItem.isShow
            ? {
                  type: NavItemType.NEXT,
                  labelText: lang('下一节'),
                  isShow: navSwitch.isSiblingsOpen || isShowEnrollment,
                  data: {
                      id: siblingsInfo.siblings.next_session_id || '',
                      url: siblingsInfo.siblings.next || '',
                      type: siblingsInfo.siblings.next_type || '',
                  },
              }
            : lastSessionItem;

    if (!siblingsInfo.siblings.prev && prevCourse) {
        prevItem = {
            type: NavItemType.PREV_COURSE,
            labelText: lang('上一门课'),
            isShow: true,
            data: {
                id: prevCourse.id,
                url: prevCourse.url,
            },
        };
    }
    if (!siblingsInfo.siblings.next && nextCourse) {
        nextItem = {
            type: NavItemType.NEXT_COURSE,
            labelText: lang('下一门课'),
            isShow: true,
            data: {
                id: nextCourse.id,
                url: nextCourse.url,
            },
        };
    }

    // 收藏
    const collectionItem = {
        type: NavItemType.COLLECTION,
        isShow: !navSwitch.isSessionTrail,
        labelText: navSwitch.isCollected ? lang('已收藏') : lang('收藏'),
        data: {
            url: siblingsInfo.siblings.next || '',
        },
    };

    // main 中的顺序为：赞赏、收藏、积分，三个都有时前两个放入menu，而且倒序输出
    // 倒序只为符合ue展示顺序，但代码里梳理为这样的顺序方便统一处理流程
    // 为了判断是否要menu，先全部填入main

    if (rewardItem.isShow) {
        ret.groupMain.push(rewardItem);
    }
    if (!navSwitch.isSessionTrail) {
        ret.groupMain.push(collectionItem);
    }

    if (pointsItem.isShow) {
        ret.groupMain.push(pointsItem);
    }
    if (ret.groupMain.length > 2 && navSwitch.isSiblingsOpen) {
        ret.menu = ret.groupMain.splice(0, 2);
    }
    if (navSwitch.isSiblingsOpen) {
        ret.groupNav = [prevItem, nextItem];
        // 如果调用方明确入参未明确需要显示目录
        if (directoryItem.isShow) {
            ret.groupNav.unshift(directoryItem);
        }
    }

    if (isShowEnrollment) {
        ret.groupMain = [prevItem, nextItem];
        if (directoryItem.isShow) {
            ret.groupMain.push(directoryItem);
        }
        ret.groupNav = [enrollmentItem];
    }
    return ret;
}
/**
 * 获取导航展示开关变量-收敛到一个函数，参数数据可扩展
 */

function SiblingsNav(props: IProps) {
    const [siblingsInfo, setSiblingsInfo] = useState<ISiblingsNavData | null>(null);
    const [dangerBottom, setDangerBottom] = useState(false);
    const [isShowRewardPanel, setIsShowRewardPanel] = useState(false);
    const [isShowMenuList, setIsShowMenuList] = useState(false);
    const [isCollected, setIsCollected] = useState(false);
    // 在iframe展示的时候，UI上对底部的bar进行平铺，展示宽度要求至少880，其余情况降级成以前的逻辑，里面有异步逻辑
    // @ref https://stackoverflow.com/questions/7808729/detect-iframe-height-and-width-from-inside-the-iframe
    const { isDisplayInIframeModeForCourse } = useMobileIframeLandscapeDisplayManager();
    const isNotShowDirectory = isDisplayInIframeModeForCourse && isInIframe();
    const elementId = props.elementId;
    const parentId = props.parentId;
    const beforeClickArrowCallback = props.beforeClickArrowCallback;
    const [isShowCatalogDrawer, setIsShowCatalogDrawer] = useState(false);

    useEffect(() => {
        fetchData();
    }, []);

    useEffect(() => {
        if (!(ua.isIphoneX && ua.isIphoneXR && ua.isIphoneXSMax)) {
            return;
        }
        const scroll = debounce(() => {
            const is = isHasIphoneXBottomToolbar();
            setDangerBottom(!is);
        }, 200);
        scroll();
        $window.addEventListener('scroll', scroll, { passive: true });
        return () => {
            $window.removeEventListener('scroll', scroll);
        };
    }, []);

    function fetchData() {
        getSiblingsNav(parentId, elementId).then((data: ISiblingsNavData) => {
            setSiblingsInfo(data);
            setIsCollected(!!(data && data.is_collected === IsCollectionAdd.true));
        });
    }

    const onMenuClick = (e: React.MouseEvent) => {
        setIsShowMenuList(!isShowMenuList);
        e.preventDefault();
        e.stopPropagation();
        return false;
    };

    const onCloseMenu = () => {
        setIsShowMenuList(false);
        $window.document.removeEventListener('click', onCloseMenu, false);
    };

    useEffect(() => {
        if (isShowMenuList) {
            $window.document.addEventListener('click', onCloseMenu, false);
        }
    }, [isShowMenuList]);

    const onClickArrow = useCallback(
        (itemParam: any) => {
            beforeClickArrowCallback?.();
            if (!itemParam?.data?.url) {
                return;
            }

            if (!isDisplayInIframeModeForCourse) {
                itemParam?.data?.url && (location.href = itemParam.data.url);
                return;
            }

            const handleWithCheckGame = (postHandle: VoidFunction) => {
                if (itemParam?.data?.type === SESSION_TYPES.GAME) {
                    openWindow(itemParam?.data?.url, '_blank');
                } else {
                    postHandle();
                }
            };

            const postMsgParam = {
                info: {
                    data: {
                        ...itemParam,
                        id: itemParam?.data.id,
                        url: itemParam?.data?.url,
                        type: itemParam?.data?.type,
                        extension: itemParam,
                    },
                },
                from: location.href,
            };

            switch (itemParam.type) {
                case NavItemType.PREV:
                    handleWithCheckGame(() => childPostControl.sendGotoPrevious(postMsgParam));
                    break;
                case NavItemType.NEXT:
                    handleWithCheckGame(() => childPostControl.sendGotoNext(postMsgParam));
                    break;
                case NavItemType.BACK_DIRECTORY:
                    childPostControl.sendGoBackDirectory(postMsgParam);
                    break;
                case NavItemType.PREV_COURSE:
                    childPostControl.sendGotoPrevCourse(postMsgParam);
                    break;
                case NavItemType.NEXT_COURSE:
                    childPostControl.sendGotoNextCourse(postMsgParam);
                    break;
                default:
                    break;
            }
        },
        [isDisplayInIframeModeForCourse, beforeClickArrowCallback],
    );

    const data = useGetNavListData({
        siblingsInfo,
        isDisplayInIframeModeForCourse,
        parentId,
        isNotShowDirectory,
    });

    function renderReward() {
        if (!isShowRewardPanel) {
            return null;
        }
        const orderData = {
            ref_id: props.elementId,
            ref_type: 'reward_session',
            app_type: 'reward_session',
            amount: 0,
            pay_channel: '',
        };
        const payChannel = props.payChannel || getModuleData('payChannel') || [];
        // const payChannel = ['paypal', 'wx_pub', 'wx_pub_qr'];
        return (
            <Reward
                payChannel={payChannel}
                orderData={orderData}
                relationId={props.parentId}
                onClose={() => setIsShowRewardPanel(false)}
            />
        );
    }

    function renderNavItem(item: INavItem) {
        // const extraClsName: string = isDisplayInIframeModeForCourse ? 'item-landscape' : '';
        if (item.type === NavItemType.POINTS) {
            let node: JSX.Element;
            if (isDisplayInIframeModeForCourse && !isInProgram) {
                node = (
                    <Tooltip
                        key={item.type}
                        placement="top"
                        getPopupContainer={(triggerNode) => triggerNode && (triggerNode.parentNode as any)}
                        overlay={<span>{item.labelText}</span>}
                    >
                        <a
                            className={`siblings-nav-item-btn`}
                            onClick={() => {
                                tryIframeRegisterPostLoginAlwaysResolve().then((isIframeLogin) => {
                                    if (isIframeLogin) {
                                        childPostControl.sendMsg({
                                            action: SessionMessageAction.openPage,
                                            data: {
                                                url: item.data.url,
                                                title: item.data.title,
                                            },
                                        });
                                    }
                                });
                            }}
                            href={'javascript:;'}
                        >
                            <span>{item.data.totalScore || 0}</span>
                        </a>
                    </Tooltip>
                );
            } else {
                node = (
                    <a
                        key={item.type}
                        className={`siblings-nav-item-btn`}
                        href={item.data.url || 'javascript:;'}
                        target="_blank"
                    >
                        {item.labelText}
                        <span>{item.data.totalScore || 0}</span>
                    </a>
                );
            }
            return (
                <div key={item.type} className={`siblings-nav-item nav-${item.type}`}>
                    {node}
                </div>
            );
        }

        if (item.type === NavItemType.REWARD) {
            return (
                <div key={item.type} className={`siblings-nav-item nav-${item.type}`}>
                    {isDisplayInIframeModeForCourse ? (
                        <Tooltip
                            getPopupContainer={(triggerNode) => triggerNode && (triggerNode.parentNode as any)}
                            placement="top"
                            overlay={<span>{item.labelText}</span>}
                        >
                            <button
                                className="siblings-nav-item-btn"
                                onClick={() => setIsShowRewardPanel(true)}
                            ></button>
                        </Tooltip>
                    ) : (
                        <button className="siblings-nav-item-btn" onClick={() => setIsShowRewardPanel(true)}>
                            {item.labelText}
                        </button>
                    )}
                </div>
            );
        }

        if (item.type === NavItemType.MENU) {
            return (
                <div key={item.type} className={`siblings-nav-item nav-${item.type}`}>
                    <button onClick={onMenuClick} className="siblings-nav-item-btn" />
                </div>
            );
        }
        if (item.type === NavItemType.ENROLLMENT) {
            return (
                <div key={item.type} className="enrollment-info-btn">
                    {item.labelText}
                </div>
            );
        }
        if (item.type === NavItemType.COLLECTION) {
            return (
                <div
                    key={item.type}
                    className={`siblings-nav-item nav-${item.type} ${isCollected ? 'is-collected' : ''}`}
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        return false;
                    }}
                >
                    <ToggleCollection
                        objectId={props.elementId}
                        objectType={ECollectionType.ELEMENT}
                        isCollected={!!isCollected}
                        onChange={(isFavor: boolean) => {
                            if (isDisplayInIframeModeForCourse) {
                                const msgPayload: IIframeSessionTriggerClickACKChild = {
                                    action: SessionMessageAction.triggerClick,
                                    data: {
                                        clickType: isFavor
                                            ? SessionTriggerClickTypes.favor
                                            : SessionTriggerClickTypes.unFavor,
                                        sessionId: props.elementId + '',
                                    },
                                };
                                childPostControl.sendMsg(msgPayload);
                            }
                            setIsCollected(isFavor);
                        }}
                    >
                        {isDisplayInIframeModeForCourse ? (
                            <Tooltip
                                placement="top"
                                getPopupContainer={(triggerNode) => triggerNode && (triggerNode.parentNode as any)}
                                overlay={<span>{item.labelText}</span>}
                            >
                                <button className={`siblings-nav-item-btn`}>{/* {item.labelText} */}</button>
                            </Tooltip>
                        ) : (
                            <button className={`siblings-nav-item-btn`}>{item.labelText}</button>
                        )}
                    </ToggleCollection>
                </div>
            );
        }

        const typeIsArrow =
            item.type === NavItemType.NEXT ||
            item.type === NavItemType.PREV ||
            item.type === NavItemType.NEXT_COURSE ||
            item.type === NavItemType.PREV_COURSE;
        if (typeIsArrow || item.type === NavItemType.DIRECTORY || item.type === NavItemType.BACK_DIRECTORY) {
            const disabled = item.data.url ? 'active' : 'disabled';
            const isDisabled = !item.data.url;
            if (isDisplayInIframeModeForCourse && typeIsArrow) {
                return (
                    <a
                        key={item.type}
                        onClick={() => {
                            onClickArrow(item);
                        }}
                        href={'javascript:;'}
                        className={`landscape landscape-btn siblings-nav-item nav-${item.type} ${disabled}`}
                    >
                        {item.type === NavItemType.PREV || item.type === NavItemType.PREV_COURSE ? (
                            <Button type="ghost" iconName="Prev" disabled={isDisabled}>
                                {item.labelText}
                            </Button>
                        ) : (
                            <Button type="primary" iconName="Next" disabled={isDisabled} isIconPositionRight>
                                {item.labelText}
                            </Button>
                        )}
                    </a>
                );
            } else if (typeIsArrow) {
                let iconNode: JSX.Element;
                if (item.type === NavItemType.PREV || item.type === NavItemType.PREV_COURSE) {
                    iconNode = <Iconfont type="Prev" />;
                } else {
                    iconNode = <Iconfont type="Next" />;
                }
                return (
                    <div key={item.type} className={`siblings-nav-item nav-${item.type} ${disabled}`}>
                        <a
                            className="siblings-nav-item-icon-btn"
                            onClick={() => {
                                onClickArrow(item);
                            }}
                            href={'javascript:;'}
                        >
                            <div className="icon">{iconNode}</div>
                            {item.labelText}
                        </a>
                    </div>
                );
            } else if (item.type === NavItemType.BACK_DIRECTORY) {
                return (
                    <If condition={!!item.isShow} key={item.type}>
                        <Button
                            type="primary"
                            key={item.type}
                            onClick={() => {
                                onClickArrow(item);
                            }}
                        >
                            {item.labelText}
                        </Button>
                    </If>
                );
            } else if (item.type === NavItemType.DIRECTORY) {
                return (
                    <div key={item.type} className={`siblings-nav-item nav-${item.type}`} id={COURSE_CATALOG_DRAWER_ID}>
                        <a
                            className="siblings-nav-item-btn"
                            onClick={() => {
                                onClickArrow(item);
                                setIsShowCatalogDrawer(true);
                            }}
                            href={'javascript:;'}
                        >
                            {item.labelText}
                        </a>
                        <CourseCatalogDrawer
                            visible={isShowCatalogDrawer}
                            parentId={String(parentId)}
                            onClose={() => {
                                setIsShowCatalogDrawer(false);
                            }}
                        />
                    </div>
                );
            }

            return (
                <div key={item.type} className={`siblings-nav-item nav-${item.type} ${disabled}`}>
                    <a
                        className="siblings-nav-item-btn"
                        onClick={() => {
                            onClickArrow(item);
                        }}
                        href={'javascript:;'}
                    >
                        {item.labelText}
                    </a>
                </div>
            );
        }

        return null;
    }

    function renderData() {
        const menu =
            data.menu.length > 0 ? (
                <div className="siblings-nav-item-menu">
                    <div className="menu-toggle">
                        {renderNavItem({
                            type: NavItemType.MENU,
                            labelText: '',
                            data: {},
                        })}
                    </div>
                    <div className="menu-list" style={{ display: isShowMenuList ? 'block' : 'none' }}>
                        <div className="menu-list-wrap">
                            {data.menu.reverse().map((item) => {
                                return renderNavItem(item);
                            })}
                        </div>
                    </div>
                </div>
            ) : null;
        // 在平铺模式下，menu的数据平铺到main的展示
        const landScapeClsName = isDisplayInIframeModeForCourse ? 'landscape' : 'normal';
        const sessionUrl = (window.pageData as any)?.data?.element_info?.share_url || location.href;
        const pcSessionUrl: string = composePcSessionUrl(siblingsInfo?.siblings?.course_url || '', sessionUrl);
        const main = (
            <div className={`siblings-nav-item-group group-main ${landScapeClsName}`}>
                {isDisplayInIframeModeForCourse ? null : menu}
                {(isDisplayInIframeModeForCourse ? [...data.menu, ...data.groupMain] : data.groupMain).map((item) => {
                    return renderNavItem(item);
                })}
                {isDisplayInIframeModeForCourse ? (
                    <Share sessionId={props.elementId + ''} className="sibling-share" sharePcUrl={pcSessionUrl} />
                ) : null}
            </div>
        );

        const nav =
            data.groupNav.length > 0 ? (
                <div className={`siblings-nav-item-group group-nav ${landScapeClsName}`}>
                    {data.groupNav.map((item) => {
                        return renderNavItem(item);
                    })}
                </div>
            ) : null;
        return {
            main,
            nav,
        };
    }

    function render() {
        if (!siblingsInfo) {
            return null;
        }

        // 在pin的iframe（这块查了源码，只要是满足umu的域名即可）及pc明确的iframe场景下展示
        if (isInIframe() && !(isInPinIFrame() || isDisplayInIframeModeForCourse)) {
            return null;
        }
        const { main, nav } = renderData();
        const siblingNavClsName = isDisplayInIframeModeForCourse ? 'landscape' : 'normal';
        return (
            <div className={classnames(`siblings-nav-container`, siblingNavClsName)}>
                <div className="siblings-nav-container-holder"></div>
                <div
                    className={classnames(
                        isDisplayInIframeModeForCourse ? 'landscape-fixed' : 'doc-fixed',
                        'bottom filled bordered',
                    )}
                >
                    <div
                        className={classnames(`siblings-nav`, siblingNavClsName, {
                            'fix-danger-bottom': dangerBottom,
                            'centered': !nav,
                            'siblings-nav-in-mini-app': isFromCourseMiniApp(),
                        })}
                    >
                        {main}
                        {nav}
                    </div>
                    {renderReward()}
                </div>
            </div>
        );
    }

    return render();
}

export default SiblingsNav;
